/**
 * Community Service
 * Handles all community-related API calls
 */

import { PAGINATION } from "@/constants/constants"
import { apiClient } from "@/lib/api-client"
import { snakeToCamelCase } from "@/utils/caseConverter"
import type {
  CommunityDetail,
  UpdateCommunityData,
  UserCommunitiesApiParams,
  UserCommunitiesResponse,
} from "../types/index"

// Types
export type AccessRole = "OWNERS" | "MODERATORS" | "RITUALISTS" | "MEMBERS"

export interface CreateCommunityRequest {
  name: string
  community_handle: string
  type: "PUBLIC" | "PRIVATE"
  is_organization?: boolean
  street_address?: string
  city?: string
  state?: string
  country?: string
  zip_code?: string
  lat?: number
  long?: number
  description: string
  tags: string[]
  website_url?: string
  instagram_url?: string
  facebook_url?: string
  twitter_url?: string
  guidelines: string
  who_can_add_community_to_rituals: AccessRole[]
  who_can_comment: AccessRole[]
  users?: Array<{ user_id: string; role_id?: string; role_ids?: string[] }>
  files?: File[]
  avatar?: File
  coverImageIndex?: number
  video_titles?: Record<string, string> // Map of file index → video title
  thumbnail_files?: File[] // Thumbnail files ordered to match thumbnail_indices
  thumbnail_indices?: number[] // File indices that have thumbnails
}

export interface CommunityResponse {
  id: string
  name: string
  handle: string
  type: "PUBLIC" | "PRIVATE"
  description: string
  guidelines: string
  createdAt: string
  updatedAt: string
}

export interface UserSearchResponse {
  users: Array<{
    id: string
    email: string
    name: string | null
    username: string | null
    city: string | null
    profilePictureUrl: string | null
    bio: string | null
    profileVisibility: "public" | "private"
    createdAt: string
  }>
  total: number
  page: number
  totalPages: number
}

export interface UserSearchParams {
  search?: string
  page?: number
  limit?: number
}

// Search All - Communities and Users
export interface SearchAllItem {
  type: "community" | "user"
  id: string
  title: string
  identifier: string | null // handle for communities, username for users
  description: string | null // description for communities, bio for users
  username: string | null
  city: string | null
  country: string | null
  profilePictureUrl: string | null
  coverImageUrl?: string | null
  createdAt: string
  bio: string | null
  organizerStatus?: "PENDING" | "Accepted" | "DECLINED" | null
  ritualOrganiserId?: string | null
  followerCount?: number // for user type
  memberCount?: number // for community type
}

export interface SearchAllParams {
  search?: string
  page?: number
  limit?: number
  sort?: "newest" | "relevance"
  ritualId?: string
}

export interface SearchAllResponse {
  items: SearchAllItem[]
  pagination: {
    page: number
    limit: number
    totalItems: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

/**
 * Create a new community
 * Supports multipart/form-data for file uploads
 */
export async function createCommunity(data: CreateCommunityRequest): Promise<CommunityResponse> {
  // Create FormData for multipart upload
  const formData = new FormData()

  // Add all text fields
  formData.append("name", data.name)
  formData.append("community_handle", data.community_handle)
  formData.append("type", data.type)
  formData.append("is_organization", String(data.is_organization ?? false))
  if (data.street_address) formData.append("street_address", data.street_address)
  if (data.city) formData.append("city", data.city)
  if (data.state) formData.append("state", data.state)
  if (data.country) formData.append("country", data.country)
  if (data.zip_code) formData.append("zip_code", data.zip_code)
  if (data.lat !== undefined) formData.append("lat", data.lat.toString())
  if (data.long !== undefined) formData.append("long", data.long.toString())
  formData.append("description", data.description)
  if (data.website_url) formData.append("website_url", data.website_url)
  if (data.instagram_url) formData.append("instagram_url", data.instagram_url)
  if (data.facebook_url) formData.append("facebook_url", data.facebook_url)
  if (data.twitter_url) formData.append("twitter_url", data.twitter_url)
  formData.append("guidelines", data.guidelines)
  formData.append(
    "who_can_add_community_to_rituals",
    JSON.stringify(data.who_can_add_community_to_rituals),
  )
  formData.append("who_can_comment", JSON.stringify(data.who_can_comment))

  // Add tags as JSON string
  formData.append("tags", JSON.stringify({ add: data.tags }))

  // Add users as JSON string
  if (data.users && data.users.length > 0) {
    formData.append("users", JSON.stringify(data.users))
  }

  // Add cover image index if provided
  if (data.coverImageIndex !== undefined) {
    formData.append("cover_image_index", data.coverImageIndex.toString())
  }

  // Add files
  if (data.files && data.files.length > 0) {
    data.files.forEach(file => {
      formData.append("files", file)
    })
  }

  // Add video titles (map of file index → title)
  if (data.video_titles && Object.keys(data.video_titles).length > 0) {
    formData.append("video_titles", JSON.stringify(data.video_titles))
  }

  // Add video thumbnail files and their index mapping
  if (data.thumbnail_files && data.thumbnail_files.length > 0) {
    data.thumbnail_files.forEach(file => {
      formData.append("media_thumbnails", file)
    })
    formData.append("thumbnail_indices", JSON.stringify(data.thumbnail_indices))
  }

  // Add avatar
  if (data.avatar) {
    formData.append("avatar", data.avatar)
  }

  const response = await apiClient.post<{
    success: boolean
    data: CommunityResponse
    message: string
  }>("/api/communities", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })

  return response.data.data
}

/**
 * Get community by ID
 */
export async function getCommunity(id: string): Promise<CommunityResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: CommunityResponse
    message: string
  }>(`/api/communities/${id}`)

  return response.data.data
}

/**
 * Get community by ID (detailed version for edit page)
 * Returns all fields including tags, members, uploads, settings
 */
export async function getCommunityById(id: string): Promise<CommunityDetail> {
  const response = await apiClient.get<{
    success: boolean
    data: CommunityDetail
    message: string
  }>(`/api/communities/${id}`)

  // Convert snake_case to camelCase
  return snakeToCamelCase<CommunityDetail>(response.data.data as unknown as Record<string, unknown>)
}

/**
 * Get community by handle
 */
export async function getCommunityByHandle(handle: string): Promise<CommunityResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: CommunityResponse
    message: string
  }>(`/api/communities/handle/${handle}`)

  return response.data.data
}

/**
 * Update community
 */
export async function updateCommunity(
  id: string,
  data: Partial<UpdateCommunityData> | FormData,
): Promise<CommunityResponse> {
  const isFormData = data instanceof FormData
  const response = await apiClient.patch<{
    success: boolean
    data: CommunityResponse
    message: string
  }>(`/api/communities/${id}`, data, {
    headers: isFormData ? { "Content-Type": "multipart/form-data" } : undefined,
  })

  return response.data.data
}

/**
 * Upload community image
 */
export async function uploadCommunityImage(
  communityId: string,
  file: File,
): Promise<{ uploadId: string; url: string }> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await apiClient.post<{
    success: boolean
    data: { uploadId: string; url: string }
    message: string
  }>(`/api/communities/${communityId}/images`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })

  return response.data.data
}

/**
 * Delete community image
 */
export async function deleteCommunityImage(communityId: string, uploadId: string): Promise<void> {
  await apiClient.delete(`/api/communities/${communityId}/images/${uploadId}`)
}

export async function replaceCommunityImage(
  communityId: string,
  uploadId: string,
  file: File,
): Promise<{ id: string; filePath: string; isCover: boolean }> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await apiClient.put<{
    success: boolean
    data: { id: string; filePath: string; isCover: boolean }
    message: string
  }>(`/api/communities/${communityId}/images/${uploadId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data.data
}

/**
 * Delete community
 */
export async function deleteCommunity(communityId: string): Promise<void> {
  await apiClient.delete(`/api/communities/${communityId}`)
}

/**
 * Get all community roles
 */
export async function getCommunityRoles(): Promise<Array<{ id: string; title: string }>> {
  const response = await apiClient.get<{
    success: boolean
    data: Array<{ id: string; title: string }>
    message: string
  }>("/api/communities/roles")

  return response.data.data
}

/**
 * Get user communities with search, filter, and pagination
 */
export async function getUserCommunities(
  params: UserCommunitiesApiParams,
): Promise<UserCommunitiesResponse> {
  const {
    userId,
    search,
    filter,
    sort,
    page = PAGINATION.DEFAULT_PAGE,
    limit = PAGINATION.COMMUNITY_LIST_LIMIT,
  } = params

  const queryParams = new URLSearchParams()
  if (search) queryParams.append("search", search)
  if (filter) queryParams.append("filter", filter)
  if (sort) queryParams.append("sort", sort)
  queryParams.append("page", page.toString())
  queryParams.append("limit", limit.toString())

  const response = await apiClient.get<{
    success: boolean
    data: UserCommunitiesResponse
    message: string
  }>(`/api/communities/user/${userId}?${queryParams.toString()}`)

  // Convert snake_case to camelCase
  return snakeToCamelCase<UserCommunitiesResponse>(
    response.data.data as unknown as Record<string, unknown>,
  )
}

/**
 * Search communities and users (for ritual organizers selection)
 * Searches on: community name/handle and user name/username
 */
export async function searchCommunitiesAndUsers(
  params: SearchAllParams = {},
): Promise<SearchAllResponse> {
  const {
    search,
    page = PAGINATION.DEFAULT_PAGE,
    limit = PAGINATION.DEFAULT_LIMIT,
    sort = "newest",
    ritualId,
  } = params

  const queryParams = new URLSearchParams()
  if (search) queryParams.append("search", search)
  queryParams.append("page", page.toString())
  queryParams.append("limit", limit.toString())
  if (sort) queryParams.append("sort", sort)
  if (ritualId) queryParams.append("ritualId", ritualId)

  const response = await apiClient.get<{
    success: boolean
    data: SearchAllResponse
    message: string
  }>(`/api/communities/search-all?${queryParams.toString()}`)

  return response.data.data
}

/**
 * Community Statistics interface
 */
export interface CommunityStatistics {
  ritualists: number
  members: number
  owners: number
  discussions: number
  // Ritual breakdown - from merged rituals/statistics
  ritualsAll: number
  ritualsArchived: number
  ritualRequests: number
}

/**
 * Get community statistics
 */
export async function getCommunityStatistics(communityId: string): Promise<CommunityStatistics> {
  const response = await apiClient.get<{
    success: boolean
    data: CommunityStatistics
    message: string
  }>(`/api/communities/${communityId}/statistics`)

  return response.data.data
}

/**
 * Community Member interface
 */
export interface CommunityMember {
  id: string
  username: string | null
  name: string | null
  coverImage: string | null
  followers: number
  isFollowing?: boolean
  isPending?: boolean
  communityBuilder?: boolean
  roleIds?: string[] // Multiple roles - array of role IDs
  roleId?: string | null // Keep for backward compatibility
  roleTitles?: string[] // All role titles
  roleTitle?: string | null
  // Saved status - whether current user saved this ritualist
  isSaved?: boolean
  // Count of rituals created by this user
  ritualsCreatedCount?: number
  // User bio/description (both supported for backward compatibility)
  bio?: string | null
  description?: string | null
}

/**
 * Get community members params
 */
export interface GetCommunityMembersParams {
  search?: string
  // Actual community roles: ritualist, moderator, owner, member
  role?: "ritualist" | "moderator" | "owner" | "member" | "all"
  // Filter to only include highlighted (saved) ritualists for the current user
  highlighted?: boolean
  page?: number
  limit?: number
}

/**
 * Community members response
 */
export interface CommunityMembersResponse {
  members: CommunityMember[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
    hasMore: boolean
  }
  meta: {
    AllRitualists: number
    highlightedRitualists: number
    totalCount: number
    MemberCount: number
    ModeratorCount: number
    OwnerCount: number
    HiddenCount: number
    requestCount: number
  }
}

/**
 * Get community members
 */
export async function getCommunityMembers(
  communityId: string,
  params?: GetCommunityMembersParams,
): Promise<CommunityMembersResponse> {
  const queryParams = new URLSearchParams()
  if (params?.search) queryParams.append("search", params.search)
  if (params?.role) queryParams.append("role", params.role)
  if (params?.highlighted !== undefined)
    queryParams.append("highlighted", params.highlighted.toString())
  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.limit) queryParams.append("limit", params.limit.toString())

  const queryString = queryParams.toString()
  const url = `/api/communities/${communityId}/members${queryString ? `?${queryString}` : ""}`

  const response = await apiClient.get<{
    success: boolean
    data: CommunityMembersResponse
    message: string
  }>(url)

  return response.data.data
}

/**
 * ========================================
 * COMMUNITY JOIN/LEAVE API FUNCTIONS
 * ========================================
 */

/**
 * Membership Status interface
 */
export interface MembershipStatus {
  isMember: boolean
  isPending: boolean
  memberSince?: string
}

/**
 * Join Request interface
 */
export interface JoinRequest {
  id: string
  requesterId: string
  requesterName: string
  requesterUsername?: string
  requesterAvatar?: string
  requestedAt: string
}

/**
 * Join Requests Response interface
 */
export interface JoinRequestsResponse {
  requests: JoinRequest[]
  total: number
  page: number
  totalPages: number
}

/**
 * Join a community
 * @param communityId - Community ID
 * @returns Result with success status and message
 */
export async function joinCommunity(
  communityId: string,
): Promise<{ success: boolean; message: string; isPending?: boolean }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string; isPending?: boolean }
    message: string
  }>(`/api/communities/${communityId}/join`)

  return response.data.data
}

/**
 * Leave a community
 * @param communityId - Community ID
 * @returns Result with success status and message
 */
export async function leaveCommunity(
  communityId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/leave`)

  return response.data.data
}

/**
 * Get community membership status
 * @param communityId - Community ID
 * @returns Membership status object
 */
export async function getCommunityMembership(communityId: string): Promise<MembershipStatus> {
  const response = await apiClient.get<{
    success: boolean
    data: MembershipStatus
    message: string
  }>(`/api/communities/${communityId}/membership`)

  return response.data.data
}

/**
 * Get join requests for a community (admin only)
 * @param communityId - Community ID
 * @param options - Query options (page, limit)
 * @returns Paginated list of join requests
 */
export async function getJoinRequests(
  communityId: string,
  options?: { page?: number; limit?: number },
): Promise<JoinRequestsResponse> {
  const queryParams = new URLSearchParams()
  if (options?.page) queryParams.append("page", options.page.toString())
  if (options?.limit) queryParams.append("limit", options.limit.toString())

  const queryString = queryParams.toString()
  const url = `/api/communities/${communityId}/requests${queryString ? `?${queryString}` : ""}`

  const response = await apiClient.get<{
    success: boolean
    data: JoinRequestsResponse
    message: string
  }>(url)

  return response.data.data
}

/**
 * Accept a join request (admin only)
 * @param communityId - Community ID
 * @param requestId - Join request ID
 * @returns Result with success status
 */
export async function acceptJoinRequest(
  communityId: string,
  requestId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/requests/${requestId}/accept`)

  return response.data.data
}

/**
 * Reject a join request (admin only)
 * @param communityId - Community ID
 * @param requestId - Join request ID
 * @param reason - Optional rejection reason
 * @returns Result with success status
 */
export async function rejectJoinRequest(
  communityId: string,
  requestId: string,
  reason?: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/requests/${requestId}/reject`, { reason })

  return response.data.data
}

/**
 * Cancel pending join request
 * @param communityId - Community ID
 * @returns Result with success status
 */
export async function cancelJoinRequest(
  communityId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/requests/cancel`)

  return response.data.data
}

/**
 * Update member roles (bulk update)
 * @param communityId - Community ID
 * @param updates - Array of userId and roleIds pairs to update
 * @returns Result with success status
 */
export async function updateMemberRoles(
  communityId: string,
  updates: Array<{ userId: string; roleId?: string; roleIds?: string[] }>,
): Promise<{ success: boolean; updated: number; message: string }> {
  const response = await apiClient.patch<{
    success: boolean
    data: { success: boolean; updated: number; message: string }
    message: string
  }>(`/api/communities/${communityId}/members/roles`, { updates })

  return response.data.data
}

/**
 * Toggle save/unsave a ritualist
 * @param ritualistId - ID of the ritualist to save/unsave
 * @returns Object with saved status
 */
export async function toggleSaveRitualist(
  ritualistId: string,
): Promise<{ saved: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { saved: boolean; message: string }
    message: string
  }>("/api/communities/saved/ritualist", { ritualistId })

  return response.data.data
}

/**
 * Remove a ritualist from a community
 * @param communityId - ID of the community
 * @param userId - ID of the user to remove
 * @returns Object with success status and message
 */
export async function removeRitualistFromCommunity(
  communityId: string,
  userId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.patch<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}`, {
    users: { remove: [userId] },
  })

  return response.data.data
}

/**
 * Invite users to a community
 * @param communityId - Community ID
 * @param userIds - Array of user IDs to invite
 * @param roleType - Role type: 'ritualist' or 'member'
 * @returns Result with success status and invite count
 */
export async function inviteUsers(
  communityId: string,
  userIds: string[],
  roleType: "ritualist" | "member",
): Promise<{ success: boolean; invited: number; message?: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; invited: number; message?: string }
    message: string
  }>(`/api/communities/${communityId}/invite`, {
    userIds,
    roleType,
  })

  return response.data.data
}

/**
 * Get pending invites for a community
 * @param communityId - Community ID
 * @returns Array of pending invites
 */
export async function getPendingInvites(communityId: string): Promise<
  {
    id: string
    userId: string
    userName: string | null
    userUsername: string | null
    userProfilePictureUrl: string | null
    roleType: string
    invitedAt: string
  }[]
> {
  const response = await apiClient.get<{
    success: boolean
    data: {
      id: string
      userId: string
      userName: string | null
      userUsername: string | null
      userProfilePictureUrl: string | null
      roleType: string
      invitedAt: string
    }[]
  }>(`/api/communities/${communityId}/invitees`)

  return response.data.data
}

/**
 * Get eligible users to invite
 * @param communityId - Community ID
 * @param roleType - Role type filter
 * @param page - Page number for pagination
 * @returns Array of eligible users
 */
export async function getEligibleUsersForInvite(
  communityId: string,
  roleType: "ritualist" | "member" = "member",
  page: number = 1,
): Promise<{
  users: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
    profile_photos?: Array<{
      id: string
      filePath: string
      isCover: boolean
      is_cover_image: boolean
    }>
    inviteStatus: "none" | "invited"
    inviteId?: string
  }[]
  hasMore: boolean
  nextPage: number | null
}> {
  const response = await apiClient.get<{
    success: boolean
    data: {
      data: Array<{
        id: string
        name: string | null
        username: string | null
        profilePictureUrl: string | null
        profile_photos?: Array<{
          id: string
          filePath: string
          isCover: boolean
          is_cover_image: boolean
        }>
        inviteStatus: "none" | "invited"
        inviteId?: string
      }>
      total: number
      page: number
      totalPages: number
    }
    message: string
  }>(`/api/communities/${communityId}/invitees/eligible`, {
    params: { roleType, page },
  })

  // response.data = { success, data: { data: [...], total, page, totalPages }, message }
  const innerData = response.data.data
  return {
    users: innerData.data || [],
    hasMore: innerData.page < innerData.totalPages,
    nextPage: innerData.page < innerData.totalPages ? innerData.page + 1 : null,
  }
}

/**
 * Cancel a pending invite
 * @param communityId - Community ID
 * @param inviteId - Invite ID to cancel
 * @returns Result with success status
 */
export async function cancelInvite(
  communityId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/invite/${inviteId}`)

  return response.data.data
}

/**
 * Resend an invite notification
 * @param communityId - Community ID
 * @param inviteId - Invite ID to resend
 * @returns Result with success status
 */
export async function resendInvite(
  communityId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/invite/${inviteId}/resend`)

  return response.data.data
}

/**
 * Accept a community invite
 * @param communityId - Community ID
 * @param inviteId - Invite ID to accept
 * @returns Result with success status
 */
export async function acceptInvite(
  communityId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/invite/${inviteId}/accept`)

  return response.data.data
}

/**
 * Reject a community invite
 * @param communityId - Community ID
 * @param inviteId - Invite ID to reject
 * @returns Result with success status
 */
export async function rejectInvite(
  communityId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/communities/${communityId}/invite/${inviteId}/reject`)

  return response.data.data
}
